/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import AddIcon from "@mui/icons-material/Add";
import CouponsAPI from "../../../api/firebase/CouponsAPI";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import AddCoupon from "../../../components/admin/add-coupon/AddCoupon";
import AllCouponsContext from "../../../context/allCoupons/AllCouponsContext";
import AddCouponContext from "../../../context/addCoupon/AddCouponContext";
import { styles } from "./styles";
import CustomDataGrid from "../../../components/common/DataGrid/CustomDataGrid";
import AdminStatCard from "../../../components/admin/stat-card/AdminStatCard";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";

// Enhanced Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CouponsListAdmin = () => {
  const initState = {
    id: "",
    couponCode: "",
    minShopping: "",
    maxUses: "",
    amount: "",
    isActive: true,
    users: [],
  };

  const couponAPI = useMemo(() => new CouponsAPI(), []);
  const allCouponsContext = useContext(AllCouponsContext);
  const couponContext = useContext(AddCouponContext);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openCouponModal, setOpenCouponModal] = useState(false);
  const [editCouponModal, setEditCouponModal] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(null);
  const [copiedCode, setCopiedCode] = useState("");

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, ACTIVE, INACTIVE

  // Snackbar notifications
  const [snackbarState, setSnackbarState] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });

  const showSnackbar = (text, type = "success") => {
    setMessage({ text, type });
    setSnackbarState(true);
  };

  const hideSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState(false);
  };

  // Fetch all coupons
  const fetchCoupons = async () => {
    setIsRefreshing(true);
    try {
      const resData = await couponAPI.getCoupons();
      if (resData && resData.data) {
        allCouponsContext.setState(resData.data);
      }
    } catch (err) {
      console.error("Error fetching coupons:", err);
      showSnackbar("Failed to fetch coupons data.", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const showAddCouponModal = (action) => {
    if (action === "ADD") {
      setEditCouponModal(false);
      couponContext.setState(initState);
    }
    setOpenCouponModal(true);
  };

  const hideAddCouponModal = () => setOpenCouponModal(false);

  const setEditCoupon = (coupon) => {
    setEditCouponModal(true);
    couponContext.setState(coupon);
    showAddCouponModal("EDIT");
  };

  // Add / Update Coupon
  const handleSetCoupon = async (
    action = "ADD",
    data = couponContext.state,
    payload = null
  ) => {
    const coupon_id = payload !== null ? data : data.id;
    const finalPayload = payload !== null ? payload : data;

    try {
      const resData = await couponAPI.setCoupon({
        id: coupon_id,
        payload: finalPayload,
      });

      if (resData && resData.success) {
        const action_msg = action === "ADD" ? "created" : "updated";
        showSnackbar(
          `Coupon "${finalPayload.couponCode || coupon_id}" ${action_msg} successfully!`,
          "success"
        );

        if (action === "ADD") {
          const newCoupon = {
            ...data,
            id: coupon_id,
            createdAt: { seconds: Math.floor(Date.now() / 1000) },
            users: data.users || [],
          };
          allCouponsContext.setState([newCoupon, ...(allCouponsContext.state || [])]);
        } else {
          const coupons_ = (allCouponsContext.state || []).map((c) =>
            c.id === coupon_id ? { ...c, ...finalPayload } : c
          );
          allCouponsContext.setState(coupons_);
        }
        couponContext.setState(initState);
        hideAddCouponModal();
      } else {
        showSnackbar("Failed to save coupon. Please try again.", "error");
      }
    } catch (ex) {
      console.error("Error saving coupon:", ex);
      showSnackbar("An error occurred while saving coupon.", "error");
    }
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    if (!deleteConfirmDialog) return;
    const couponToDelete = deleteConfirmDialog;
    try {
      const res = await couponAPI.deleteCoupon(couponToDelete.id);
      if (res && res.success) {
        showSnackbar(`Coupon "${couponToDelete.couponCode}" deleted successfully!`, "success");
        const updated = (allCouponsContext.state || []).filter(
          (c) => c.id !== couponToDelete.id
        );
        allCouponsContext.setState(updated);
        hideAddCouponModal();
      } else {
        showSnackbar("Failed to delete coupon.", "error");
      }
    } catch (err) {
      console.error("Error deleting coupon:", err);
      showSnackbar("An error occurred while deleting the coupon.", "error");
    } finally {
      setDeleteConfirmDialog(null);
    }
  };

  const handleCopyCode = (code, event) => {
    if (event) event.stopPropagation();
    navigator.clipboard?.writeText?.(code);
    setCopiedCode(code);
    setTimeout(() => {
      setCopiedCode("");
    }, 1800);
  };

  // KPI Calculations
  const couponsArr = allCouponsContext.state || [];

  const activeCount = useMemo(() => {
    return couponsArr.filter((c) => c.isActive !== false).length;
  }, [couponsArr]);

  const inactiveCount = useMemo(() => {
    return couponsArr.length - activeCount;
  }, [couponsArr, activeCount]);

  const totalRedemptions = useMemo(() => {
    return couponsArr.reduce((sum, c) => sum + (Array.isArray(c.users) ? c.users.length : 0), 0);
  }, [couponsArr]);

  const maxDiscount = useMemo(() => {
    return couponsArr.reduce((max, c) => Math.max(max, Number(c.amount || 0)), 0);
  }, [couponsArr]);

  // Filtered Coupons
  const filteredCoupons = useMemo(() => {
    return couponsArr.filter((coupon) => {
      // 1. Search Query
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const matches = [
          coupon.couponCode,
          coupon.id,
          String(coupon.amount || ""),
          String(coupon.minShopping || ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

        if (!matches) return false;
      }

      // 2. Status Filter
      if (statusFilter === "ACTIVE" && coupon.isActive === false) return false;
      if (statusFilter === "INACTIVE" && coupon.isActive !== false) return false;

      return true;
    }).sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
  }, [couponsArr, searchQuery, statusFilter]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setStatusFilter("ALL");
  };

  const hasActiveFilters = searchQuery.trim() !== "" || statusFilter !== "ALL";

  // DataGrid Columns Definition
  const columns = [
    {
      field: "couponCode",
      headerName: "Coupon Code",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const code = params.row.couponCode || params.row.id || "";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.8,
                px: 1.2,
                py: 0.5,
                borderRadius: "8px",
                bgcolor: "#f1f5f9",
                border: "1px dashed #cbd5e1",
                fontFamily: "ui-monospace, monospace",
                fontWeight: 800,
                fontSize: "0.85rem",
                color: "#0f172a",
                letterSpacing: "0.04em",
              }}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: 15, color: "#004d40" }} />
              {code}
            </Box>

            <Tooltip title={copiedCode === code ? "Copied!" : "Copy Code"}>
              <IconButton
                size="small"
                onClick={(e) => handleCopyCode(code, e)}
                sx={{
                  color: "#94a3b8",
                  p: 0.5,
                  "&:hover": { color: "#004d40", bgcolor: "rgba(0,77,64,0.06)" },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "amount",
      headerName: "Discount Value",
      width: 150,
      valueGetter: (params) => Number(params.row.amount || 0),
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 800, color: "#047857", fontSize: "0.9rem" }}
          >
            ₹{Number(params.row.amount || 0).toLocaleString("en-IN")} OFF
          </Typography>
        );
      },
    },
    {
      field: "minShopping",
      headerName: "Min. Cart Value",
      width: 160,
      valueGetter: (params) => Number(params.row.minShopping || 0),
      renderCell: (params) => {
        const min = Number(params.row.minShopping || 0);
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
            {min > 0 ? `Min. ₹${min.toLocaleString("en-IN")}` : "No Minimum"}
          </Typography>
        );
      },
    },
    {
      field: "maxUses",
      headerName: "Usage & Redemptions",
      width: 200,
      valueGetter: (params) => Array.isArray(params.row.users) ? params.row.users.length : 0,
      renderCell: (params) => {
        const redeemed = Array.isArray(params.row.users) ? params.row.users.length : 0;
        const max = Number(params.row.maxUses || 0);
        const isDepleted = max > 0 && redeemed >= max;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={`${redeemed} / ${max || "∞"} used`}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "0.72rem",
                height: 24,
                bgcolor: isDepleted ? "#fef2f2" : "#f8fafc",
                color: isDepleted ? "#b91c1c" : "#475569",
                border: `1px solid ${isDepleted ? "#fecaca" : "#e2e8f0"}`,
              }}
            />
            {isDepleted && (
              <Chip
                label="Full"
                size="small"
                sx={{
                  fontWeight: 800,
                  fontSize: "0.65rem",
                  height: 20,
                  bgcolor: "#dc2626",
                  color: "#ffffff",
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const active = params.row.isActive !== false;
        return (
          <Chip
            icon={
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: active ? "#10b981" : "#94a3b8",
                  mr: -0.5,
                }}
              />
            }
            label={active ? "Active" : "Inactive"}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 700,
              bgcolor: active ? "#ecfdf5" : "#f1f5f9",
              color: active ? "#047857" : "#64748b",
              border: `1px solid ${active ? "#a7f3d0" : "#e2e8f0"}`,
            }}
          />
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      width: 180,
      valueGetter: (params) => {
        const c = params.row.createdAt;
        if (typeof c === "string") return new Date(c);
        if (c?.seconds) return new Date(c.seconds * 1000);
        return new Date(0);
      },
      renderCell: (params) => {
        const t = formatDateTimeForAdmin(params.row.createdAt);
        return (
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
            {t}
          </Typography>
        );
      },
    },
    { field: "id", hide: true },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      {/* 1. Hero Header Banner matching Products, Orders, and Users */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 4,
          color: "#ffffff",
          background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)",
          boxShadow: "0 10px 25px -5px rgba(0, 77, 64, 0.3)",
          display: { xs: "none", sm: "flex" },
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.75rem", sm: "2.2rem" },
              letterSpacing: "-0.02em",
            }}
          >
            Coupons & Discounts
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.5, maxWidth: 580 }}>
            Create and manage promotional discount vouchers, minimum shopping thresholds, and customer redemption limits.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ zIndex: 1, alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          <Tooltip title="Refresh Coupons Data">
            <IconButton
              onClick={fetchCoupons}
              disabled={isRefreshing}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#ffffff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => showAddCouponModal("ADD")}
            sx={{
              bgcolor: "#ffffff",
              color: "#004d40",
              fontWeight: 800,
              textTransform: "none",
              borderRadius: "10px",
              px: 2.5,
              py: 1.1,
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            Add Coupon
          </Button>
        </Stack>
      </Box>

      {/* 2. Interactive KPI Metric Cards */}
      <Grid2 container spacing={{ xs: 1.25, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* Total Coupons */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Total Coupons"
            value={couponsArr.length}
            note={`${activeCount} Active • ${inactiveCount} Inactive`}
            icon={<LocalOfferOutlinedIcon />}
            onClick={handleResetFilters}
          />
        </Grid2>

        {/* Active Vouchers */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Active & Live"
            value={activeCount}
            note="Live on storefront checkout"
            icon={<CheckCircleOutlineRoundedIcon />}
            color="#047857"
            background="#ecfdf5"
            selected={statusFilter === "ACTIVE"}
            onClick={() => setStatusFilter(statusFilter === "ACTIVE" ? "ALL" : "ACTIVE")}
            delay={70}
          />
        </Grid2>

        {/* Total Redemptions */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Total Redemptions"
            value={totalRedemptions}
            note="Customer checkout redemptions"
            icon={<ConfirmationNumberOutlinedIcon />}
            color="#6d28d9"
            background="#f5f3ff"
            delay={140}
          />
        </Grid2>

        {/* Highest Savings */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Max Discount"
            value={`₹${maxDiscount.toLocaleString("en-IN")}`}
            note="Highest single voucher value"
            icon={<CurrencyRupeeRoundedIcon />}
            color="#1d4ed8"
            background="#eff6ff"
            selected={statusFilter === "INACTIVE"}
            onClick={() => setStatusFilter(statusFilter === "INACTIVE" ? "ALL" : "INACTIVE")}
            delay={210}
          />
        </Grid2>
      </Grid2>

      {/* 3. Filter and Search Bar */}
      <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3, mb: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search by coupon code, discount amount, or min cart value..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 },
              }}
            />

            {/* Status Filter Dropdown */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="coupon-status-filter-label">Voucher Status</InputLabel>
              <Select
                labelId="coupon-status-filter-label"
                value={statusFilter}
                label="Voucher Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
              >
                <MenuItem value="ALL">All Coupons</MenuItem>
                <MenuItem value="ACTIVE">Active Only</MenuItem>
                <MenuItem value="INACTIVE">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Active Filter Pills Bar */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
                Active Filters:
              </Typography>

              {searchQuery && (
                <Chip
                  size="small"
                  label={`Search: "${searchQuery}"`}
                  onDelete={() => setSearchQuery("")}
                  sx={{ borderRadius: 1.5 }}
                />
              )}

              {statusFilter !== "ALL" && (
                <Chip
                  size="small"
                  label={`Status: ${statusFilter === "ACTIVE" ? "Active Vouchers" : "Inactive Vouchers"}`}
                  onDelete={() => setStatusFilter("ALL")}
                  sx={{ borderRadius: 1.5 }}
                />
              )}

              <Chip
                size="small"
                label="Clear all"
                onClick={handleResetFilters}
                color="error"
                variant="outlined"
                sx={{ borderRadius: 1.5, fontWeight: 700 }}
              />
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* 4. Coupons DataGrid */}
      <CustomDataGrid
        rows={filteredCoupons}
        columns={columns}
        styles={{
          height: "600px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.03)",
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "amount", sort: "asc" }],
          },
        }}
        pageSizes={[50, 100]}
        onRowClickHandle={(params, event) => {
          if (event.target.closest('a, button, [role="button"]')) {
            return;
          }
          setEditCoupon(params.row);
        }}
      />

      {/* Add / Edit Coupon Modal */}
      <Modal
        aria-labelledby="coupon-modal-title"
        aria-describedby="coupon-modal-description"
        open={openCouponModal}
        onClose={hideAddCouponModal}
        closeAfterTransition
      >
        <Fade in={openCouponModal}>
          <Box sx={styles.boxStyle}>
            <AddCoupon
              action={editCouponModal ? "EDIT" : "ADD"}
              hideModal={hideAddCouponModal}
              handleSetCoupon={handleSetCoupon}
              onDelete={(coupon) => setDeleteConfirmDialog(coupon)}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirmDialog)}
        onClose={() => setDeleteConfirmDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: "50%",
              bgcolor: "#fef2f2",
              color: "#dc2626",
              display: "flex",
            }}
          >
            <WarningAmberRoundedIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Delete Coupon?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ color: "#475569", fontSize: "0.95rem" }}>
            Are you sure you want to permanently delete coupon{" "}
            <strong>"{deleteConfirmDialog?.couponCode}"</strong>? Customers will no longer be able to use this promotional code.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1.5, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteConfirmDialog(null)}
            sx={{
              borderRadius: "10px",
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 700,
              textTransform: "none",
              px: 2.5,
              py: 0.9,
              fontSize: "0.875rem",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#dc2626",
              px: 2.8,
              py: 0.9,
              fontSize: "0.875rem",
              boxShadow: "0 2px 8px rgba(220, 38, 38, 0.25)",
              "&:hover": { bgcolor: "#b91c1c" },
            }}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbarState}
        autoHideDuration={3500}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={message.type || "success"}
          sx={{ width: "100%", borderRadius: "10px", fontWeight: 600 }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponsListAdmin;
